import {
    asBoolean,
    asNumber,
    asString,
    loadedDefinedMap,
    loadedDefinedMapWithDefault,
} from "common/components/component-helpers";
import { Corners, ImageAspectRatio } from "common/components/image-types";
import { evalSummary } from "common/components/portable-renderers";
import { ActionSource, asUI } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { CardFloatStyle } from "component-utils/card-types";
import { TextSize, TextStyle } from "component-utils/text-types";
import { useForce } from "hooks/use-force";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useChangeEmptyness, useListItemSizeChange } from "webapp/ui/smart/list/list-lib";
import CardItem from "../ui/smart/card-item/card-item";

export const CardItemMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [
        [
            titleVP,
            subtitleVP,
            headerVP,
            ,
            sizePV,
            onTapPV,
            ,
            numTextLinesPV,
            cornersPV,
            floatStylePV,
            textSizePV,
            textStylePV,
            isHorizontalPV,
            ,
            topLeftOverlayPV,
            topRightOverlayPV,
            bottomLeftOverlayPV,
            bottomRightOverlayPV,
        ],
        [, , , imageVP],
    ] = useForceValueProducers(getValueProducers, force, ctx);

    // eslint-disable-next-line prefer-const
    let { title, subtitle, imageSource } = evalSummary(titleVP, subtitleVP, imageVP, force);
    const header = loadedDefinedMapWithDefault(headerVP, "", asString);
    const numTextLines = loadedDefinedMapWithDefault(numTextLinesPV, 3, asNumber);
    const corners = loadedDefinedMapWithDefault(cornersPV, Corners.Rounded, asString) as Corners;
    const textSize = loadedDefinedMapWithDefault(textSizePV, TextSize.Medium, asString) as TextSize;
    const textStyle = loadedDefinedMapWithDefault(textStylePV, TextStyle.Normal, asString) as TextStyle;
    const size = loadedDefinedMapWithDefault(sizePV, ImageAspectRatio.Square, asString) as ImageAspectRatio;
    const floatStyle = loadedDefinedMapWithDefault(floatStylePV, CardFloatStyle.Float, asString) as CardFloatStyle;

    const { onClick, targetURL, autoRunInTabletMode } = useOnClickFromAction(onTapPV, ActionSource.GridItem, ctx, env);

    if (env.needsPush && autoRunInTabletMode && onClick !== undefined) {
        void onClick(false);
    }

    const hasHeaderDep = useChangeEmptyness(header);
    const hasTitleDep = useChangeEmptyness(title);
    const hasSubtitleDep = useChangeEmptyness(subtitle);
    const subtitleDep = numTextLines > 1 ? subtitle : undefined;

    useListItemSizeChange(hasHeaderDep, hasTitleDep, hasSubtitleDep, subtitleDep);

    return (
        <CardItem
            title={title}
            subtitle={subtitle}
            header={header}
            hasImage={imageVP !== undefined}
            imageSource={imageSource}
            onClick={onClick}
            targetURL={targetURL}
            numTextLines={numTextLines}
            corners={corners}
            textSize={textSize}
            textStyle={textStyle}
            columnWidth={env.columnWidth}
            size={size}
            floatStyle={floatStyle}
            isHorizontal={loadedDefinedMap(isHorizontalPV, asBoolean) ?? false}
            topLeftOverlay={loadedDefinedMap(topLeftOverlayPV, o => asUI(o).render(ctx, env))}
            bottomLeftOverlay={loadedDefinedMap(bottomLeftOverlayPV, o => asUI(o).render(ctx, env))}
            bottomRightOverlay={loadedDefinedMap(bottomRightOverlayPV, o => asUI(o).render(ctx, env))}
            topRightOverlay={loadedDefinedMap(topRightOverlayPV, o => asUI(o).render(ctx, env))}
            appEnvironment={env.appEnvironment}
        />
    );
};
